import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Workout } from "../../libs/workout/types";
import { getMuscleColorUnique } from "./muscleConfig";
import { getSetWeekAmountForBodyDistrict } from "./muscleUtils";
import { Exercise as ExerciseFromLib } from "../../libs/exercises/types";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

type MuscleVolumeChartProps = {
  workout: Workout;
  weeksColumns: string[];
  districts: string[];
  visibleDistricts: string[];
  onToggleDistrict: (district: string) => void;
  onSelectAll?: () => void;
  onReset?: () => void;
  exercisesLookup?: Map<string, ExerciseFromLib>;
};

export const MuscleVolumeChart: React.FC<MuscleVolumeChartProps> = ({
  workout,
  weeksColumns,
  districts,
  visibleDistricts,
  onToggleDistrict,
  onSelectAll,
  onReset,
  exercisesLookup,
}) => {
  const data = {
    labels: weeksColumns,
    datasets: districts.map((district) => {
      const color = getMuscleColorUnique(district);
      const isVisible = visibleDistricts.includes(district);
      return {
        label: district,
        data: weeksColumns.map((_, i) =>
          getSetWeekAmountForBodyDistrict(workout, district, i, exercisesLookup)
        ),
        borderColor: color,
        backgroundColor: color,
        pointBackgroundColor: color,
        pointBorderColor: color,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
        fill: false,
        tension: 0,
        hidden: !isVisible,
      };
    }),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "nearest" as const,
      intersect: true,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          title: (items: any[]) => {
            if (!items.length) return "";
            return items[0].label;
          },
          label: (item: any) => {
            return `${item.dataset.label}: ${item.formattedValue}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: "#374151",
        },
        ticks: {
          color: "#9ca3af",
          font: { size: 11 },
        },
        border: {
          color: "#374151",
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: "#374151",
        },
        ticks: {
          color: "#9ca3af",
          font: { size: 11 },
        },
        border: {
          color: "#374151",
        },
      },
    },
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-100">Volume Chart</h3>
      </div>
      <div className="flex gap-6 items-center">
        <div className="flex-1 min-w-0 pr-6" style={{ height: 400 }}>
          <Line data={data} options={options} />
        </div>
        {/* Legenda verticale a destra */}
        <div className="flex flex-col gap-1 justify-center flex-shrink-0 pl-4 border-l border-gray-700">
          {districts.map((district) => {
            const isVisible = visibleDistricts.includes(district);
            const color = getMuscleColorUnique(district);
            return (
              <button
                key={district}
                onClick={() => onToggleDistrict(district)}
                className={`flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded text-left transition-all ${
                  isVisible ? "text-gray-200" : "text-gray-600 line-through"
                }`}
              >
                <span
                  className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: color, opacity: isVisible ? 1 : 0.2 }}
                />
                {district}
              </button>
            );
          })}
          {(onSelectAll || onReset) && (
            <div className="flex gap-2 mt-2 pt-2 border-t border-gray-700">
              {onSelectAll && (
                <button
                  onClick={onSelectAll}
                  className="text-[10px] px-2 py-1 rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
                >
                  Tutti
                </button>
              )}
              {onReset && (
                <button
                  onClick={onReset}
                  className="text-[10px] px-2 py-1 rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
                >
                  Reset
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
